import React from 'react'
import { useTranslation } from 'react-i18next'
import { FormLabel, Box, Switch, Tooltip, SwitchProps, useTheme, useMediaQuery } from '@mui/material'
import { styled } from '@mui/material/styles'

const StyledSwitch = React.forwardRef<HTMLButtonElement, SwitchProps>(function StyleSwitch(props, ref) {
    return <Switch focusVisibleClassName='.Mui-focusVisible' disableRipple ref={ref} {...props} />
})

const WatchListsSwitch = styled(StyledSwitch)(({ theme }) => {
    const width = 52
    const height = width * 0.48

    const iconColor = theme.palette.primary.contrastText

    return {
        width,
        height,
        padding: 0,
        overflow: 'initial',
        '& .MuiSwitch-switchBase': {
            padding: 0,
            transitionDuration: '300ms',
            '&.Mui-checked': {
                transform: `translateX(${width * 0.4}px)`,
                color: '#fff',
                '& + .MuiSwitch-track': {
                    backgroundColor: theme.palette.primary.main,
                },
                '& .MuiSwitch-thumb': {
                    backgroundColor: theme.palette.primary.main,
                },
            },
            '&.Mui-focusVisible .MuiSwitch-thumb': {
                outline: '2px solid #000',
                textShadow: '0 0 3px #000',
            },
            '& .MuiSwitch-thumb::after': {
                backgroundImage: `url('data:image/svg+xml;charset=UTF-8, <svg xmlns="http://www.w3.org/2000/svg" width="24" height="24" viewBox="0 0 24 24" fill="${iconColor.replace('#', '%23')}" stroke="${iconColor.replace('#', '%23')}" stroke-width="2" stroke-linecap="round" stroke-linejoin="round" class="lucide lucide-star-icon lucide-star"><path d="M11.525 2.295a.53.53 0 0 1 .95 0l2.31 4.679a2.123 2.123 0 0 0 1.595 1.16l5.166.756a.53.53 0 0 1 .294.904l-3.736 3.638a2.123 2.123 0 0 0-.611 1.878l.882 5.14a.53.53 0 0 1-.771.56l-4.618-2.428a2.122 2.122 0 0 0-1.973 0L6.396 21.01a.53.53 0 0 1-.77-.56l.881-5.139a2.122 2.122 0 0 0-.611-1.879L2.16 9.795a.53.53 0 0 1 .294-.906l5.165-.755a2.122 2.122 0 0 0 1.597-1.16z"/></svg>')`,
                height: '20px',
                width: '100%',
                backgroundSize: '15px 15px',
                backgroundPosition: 'center',
                backgroundRepeat: 'no-repeat',
                content: '""',
                textAlign: 'center',
                display: 'block',
                fontWeight: 'bold',
            },
            '& .MuiSwitch-thumb': {
                backgroundColor: theme.palette.grey[500],
                borderRadius: 8,
                padding: 2,
                boxSizing: 'border-box',
                width: width * 0.65,
                height,
                transition: theme.transitions.create(['opacity'], {
                    duration: 100,
                }),
            },
        },
        '& .MuiSwitch-track': {
            borderRadius: 8,
            backgroundColor: theme.palette.grey[400],
            opacity: 0.5,
            transition: theme.transitions.create(['opacity'], {
                duration: 100,
            }),
        },
    }
})

function ToggleWatchLists({ onChange, value }: { onChange: (v: boolean) => void; value: boolean }) {
    const { t } = useTranslation()
    const theme = useTheme()
    const isSmallScreen = useMediaQuery(theme.breakpoints.down('md'))
    const handleToggle = () => {
        onChange(!value)
    }

    return (
        <Box
            component='fieldset'
            sx={{
                // border and margin is set in order to imitate MUI form fields
                border: '1px solid rgba(0, 0, 0, 0.23)',
                borderRadius: '4px',
                marginTop: '-9px',
                marginBottom: '10px',
                paddingTop: 0,
                paddingBottom: 0,
                height: `calc(100% + -7px)`,
            }}
        >
            <Box component='legend'>
                <FormLabel sx={{ fontSize: '0.75em' }}>{t('pages.watchLists.toggle.label')}</FormLabel>
            </Box>
            <Box
                sx={{
                    alignContent: 'space-around',
                    paddingTop: '4px',
                    paddingBottom: '16px',
                    display: 'flex',
                    gap: '26px 0',
                    flexFlow: 'wrap',
                }}
            >
                <Box
                    sx={{
                        px: 2,
                        textAlign: 'center',
                    }}
                >
                    <Tooltip title={t('site.filters.watchListOnly')}>
                        <WatchListsSwitch
                            onChange={handleToggle}
                            checked={value}
                            slotProps={{
                                input: {
                                    'aria-label': t('site.filters.watchListOnlyAria'),
                                },
                            }}
                        />
                    </Tooltip>
                </Box>
                {!isSmallScreen ? (
                    <span style={{ alignContent: 'center' }}>{t('pages.watchLists.toggle.watches')}</span>
                ) : null}
            </Box>
        </Box>
    )
}

export default ToggleWatchLists
